import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Code2,
  Landmark,
  GraduationCap,
  Palette,
  Lock,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/hooks/useT";

/**
 * Landing-page section: "Live previews · one per category"
 *
 * Pulls every published preview from /api/explore and surfaces the most
 * recently published path in each of the five canonical categories.
 * Renders a 5-card responsive grid → "Explore more live previews" CTA.
 *
 * Why on the landing page? AEO crawlers walk from oakaiapp.com → these
 * 5 internal links → /p/{slug}. Each card is a deep entry point into a
 * full SEO-grade preview, multiplying the surface area Google/Bing can
 * index from a single homepage hit.
 */
const ORDER = ["Business", "Tech", "Other", "Finance", "Academic"];

const META = {
  Business: { Icon: Briefcase, accent: "#0044FF", tint: "rgba(0,68,255,.10)", border: "rgba(0,68,255,.32)" },
  Tech:     { Icon: Code2,      accent: "#00FF9D", tint: "rgba(0,255,157,.10)", border: "rgba(0,255,157,.32)" },
  Other:    { Icon: Palette,    accent: "#ffb067", tint: "rgba(255,176,103,.10)", border: "rgba(255,176,103,.32)" },
  Finance:  { Icon: Landmark,   accent: "#FFD86B", tint: "rgba(255,216,107,.10)", border: "rgba(255,216,107,.32)" },
  Academic: { Icon: GraduationCap, accent: "#b9a8ff", tint: "rgba(185,168,255,.10)", border: "rgba(185,168,255,.32)" },
};

export default function LivePreviewShowcase() {
  const { t } = useT();
  const [picks, setPicks] = useState([]);
  const [stats, setStats] = useState({ total: 0, addedThisWeek: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/explore")
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.items || [];
        // Pick the most-recent path in each ordered category. Items
        // already arrive sorted by published_at DESC from the API.
        const byCat = new Map();
        for (const it of items) {
          const c = it.category || "Other";
          if (!byCat.has(c)) byCat.set(c, it);
        }
        const ordered = ORDER.map((c) => ({ category: c, item: byCat.get(c) })).filter(
          (row) => row.item,
        );
        setPicks(ordered);
        setStats({
          total: res.data?.total || items.length,
          addedThisWeek: res.data?.added_this_week || 0,
        });
        setLoading(false);
      })
      .catch(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className="px-6 md:px-10 max-w-7xl mx-auto pb-16 md:pb-24"
      data-testid="landing-live-previews"
    >
      <div className="flex items-end justify-between gap-4 mb-6 md:mb-8 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2 flex-wrap">
            <Sparkles size={11} className="text-[#00FF9D]" />
            {t("showcase_eyebrow")}
            {stats.total > 0 && (
              <span
                className="ml-1 inline-flex items-center gap-1.5 text-[10.5px] tracking-wide font-display font-medium text-white/55"
                data-testid="preview-stats"
              >
                <span className="hidden sm:inline text-white/30">·</span>
                <span className="text-white/75">{t("showcase_paths_count").replace("{n}", stats.total)}</span>
                {stats.addedThisWeek > 0 && (
                  <>
                    <span className="text-white/30">·</span>
                    <span className="inline-flex items-center gap-1 text-[#00FF9D]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#00FF9D] animate-pulse" />
                      {t("showcase_added_this_week").replace("{n}", stats.addedThisWeek)}
                    </span>
                  </>
                )}
              </span>
            )}
          </div>
          <h2 className="font-display font-semibold tracking-tight text-2xl md:text-3xl leading-tight max-w-2xl">
            {t("showcase_title")}
            <span className="block text-white/55 font-normal text-base md:text-lg mt-1.5">
              {t("showcase_subtitle")}
            </span>
          </h2>
        </div>
        <Link
          to="/explore"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-white/15 hover:border-[#00FF9D]/55 bg-white/[0.03] hover:bg-[#00FF9D]/[0.08] text-white/80 hover:text-[#9bffcc] text-sm font-display font-semibold transition shrink-0"
          data-testid="btn-explore-more-previews"
        >
          {t("showcase_explore_more")} <ArrowRight size={14} />
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {ORDER.map((c) => (
            <div
              key={c}
              className="glass p-5 h-44 animate-pulse"
              data-testid={`preview-skeleton-${c.toLowerCase()}`}
            />
          ))}
        </div>
      ) : picks.length === 0 ? (
        <div className="glass p-6 text-white/55 text-sm" data-testid="preview-empty">
          {t("showcase_empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {picks.map(({ category, item }) => {
            const m = META[category] || META.Other;
            const { Icon } = m;
            return (
              <Link
                key={item.slug}
                to={`/p/${item.slug}`}
                className="group glass p-5 flex flex-col gap-3 hover:-translate-y-1 transition-transform border border-white/10 hover:border-white/25 min-w-0"
                style={{ background: `linear-gradient(180deg, ${m.tint} 0%, rgba(255,255,255,0.02) 100%)` }}
                data-testid={`preview-card-${category.toLowerCase()}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-display font-semibold tracking-wide uppercase"
                    style={{ color: m.accent, border: `1px solid ${m.border}` }}
                  >
                    <Icon size={11} />
                    {category}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] text-white/45">
                    <Lock size={9} /> {t("showcase_free_pct")}
                  </span>
                </div>
                <div
                  className="font-display font-semibold text-[15px] leading-snug text-white/95 group-hover:text-white line-clamp-3 min-w-0 break-words"
                  title={item.goal}
                >
                  {item.goal}
                </div>
                <div className="text-[12.5px] text-white/55 leading-relaxed line-clamp-3 min-w-0 break-words">
                  {item.summary}
                </div>
                <div
                  className="mt-auto pt-2 inline-flex items-center gap-1 text-[11.5px] font-display font-medium opacity-80 group-hover:opacity-100 transition"
                  style={{ color: m.accent }}
                >
                  {t("showcase_read_preview")} <ArrowRight size={11} />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
